import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { MaskReveal, Rise, Overline } from "@/components/Reveal";
import { HOME } from "@/constants/testIds";

const HERO_IMAGE = "https://images.unsplash.com/photo-1625604086988-6e41981275fa?auto=format&fit=crop&w=1600&q=80";
const HERO_IMAGE_MOBILE = "https://images.unsplash.com/photo-1625604086988-6e41981275fa?auto=format&fit=crop&w=800&q=70";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[92vh] md:min-h-screen w-full overflow-hidden hero-hatch">
      {/* Parallax image column — DESKTOP/TABLET only (absolute right side) */}
      <motion.div
        style={{ y, scale }}
        className="hidden md:block absolute right-4 md:right-10 top-24 md:top-28 bottom-16 w-[38%] lg:w-[34%]"
      >
        <div className="clip-frame grain w-full h-full">
          <img
            src={HERO_IMAGE}
            alt="Aged beef cuts arranged on dark wood"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10 pt-32 md:pt-48 pb-16 md:pb-24 min-h-[92vh] md:min-h-screen flex flex-col justify-between">
        <div className="grid grid-cols-12 gap-6">
          <motion.div style={{ opacity }} className="col-span-12 md:col-span-8">
            <div className="hidden md:block">
              <MaskReveal><Overline>Slaughterers · Wholesale Butchers · Wiltshire</Overline></MaskReveal>
            </div>

            <h1
              data-testid={HOME.heroHeading}
              className="mt-6 md:mt-8 font-display tracking-tightest leading-[0.86] text-[18vw] sm:text-[15vw] md:text-[11vw] lg:text-[10.5vw] text-[color:var(--ink)]"
            >
              <MaskReveal delay={0.15}><span>F. Drury</span></MaskReveal>
              <MaskReveal delay={0.30}>
                <span className="italic text-[color:var(--oxblood)]">&amp; Sons.</span>
              </MaskReveal>
            </h1>

            {/* Mobile-only inline image directly under the display heading */}
            <div className="md:hidden mt-8">
              <MaskReveal delay={0.45}>
                <div className="clip-frame grain aspect-[16/11] w-full">
                  <img
                    src={HERO_IMAGE_MOBILE}
                    alt="Aged beef cuts arranged on dark wood"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </MaskReveal>
            </div>

            <div className="mt-10 md:mt-16 max-w-xl">
              <MaskReveal delay={0.55}>
                <p className="font-body text-lg md:text-2xl leading-snug text-[color:var(--ink-light)]">
                  A Wiltshire abattoir and wholesale butcher — over a hundred years of quiet craft, welfare and full-chain traceability.
                </p>
              </MaskReveal>
            </div>
          </motion.div>
        </div>

        <Rise delay={0.75} y={20} className="mt-12 md:mt-20">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
            <div className="flex flex-wrap gap-3">
              <Link to="/wholesale" data-testid={HOME.heroCta} className="btn-sharp">
                <span>Wholesale accounts</span>
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none"><path d="M0 5h13M9 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" /></svg>
              </Link>
              <Link to="/about" className="btn-sharp-outline">Our heritage</Link>
            </div>
            <div className="flex items-end gap-6">
              <div>
                <div className="font-display italic text-5xl md:text-7xl leading-none text-[color:var(--oxblood)]">100<span className="text-[color:var(--ink)]">+</span></div>
                <div className="font-ui text-[10px] uppercase tracking-editorial text-[color:var(--ink-light)] mt-2">Years of trading</div>
              </div>
              <div className="h-14 w-px bg-[color:var(--hairline)]" />
              <div>
                <div className="font-display italic text-5xl md:text-7xl leading-none text-[color:var(--oxblood)]">35</div>
                <div className="font-ui text-[10px] uppercase tracking-editorial text-[color:var(--ink-light)] mt-2">Craftspeople</div>
              </div>
            </div>
          </div>
        </Rise>
      </div>

      <div className="pointer-events-none hidden md:flex absolute left-6 md:left-10 bottom-8 items-center gap-3 z-10">
        <div className="h-8 w-px bg-[color:var(--ink)] animate-pulse" />
        <span className="font-ui text-[10px] uppercase tracking-editorial text-[color:var(--ink-light)]">Scroll</span>
      </div>
    </section>
  );
}
